import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckSquare,
  Pencil,
  Plus,
  Square,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { ProgressBar } from '../../components/common/ProgressBar';
import { PotRow } from '../../components/pots/PotRow';
import { Button } from '../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import {
  useAddChecklistItemMutation,
  useCancelGoalMutation,
  useCompleteGoalMutation,
  useDeleteGoalMutation,
  useGoalProgressQuery,
  useGoalQuery,
  useToggleChecklistItemMutation,
} from '../../features/goals/queries';
import { usePotsQuery } from '../../features/pots/queries';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { formatDateLabelFromIso } from '../../utils/goalWizardUtils';
import { useMobilePageHeaderTitle } from '../../store/MobilePageHeaderContext';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { paths } from '../../routes/paths';

export function GoalDetail() {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [completeSuggestOpen, setCompleteSuggestOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: goal, isPending, isFetching } = useGoalQuery(goalId);
  const goalShell = useQueryShellState({ data: goal, isPending, isFetching });
  useMobilePageHeaderTitle(goal?.name);
  const { data: progress } = useGoalProgressQuery(goalId);
  const { data: pots = [] } = usePotsQuery();
  const completeMutation = useCompleteGoalMutation();
  const cancelMutation = useCancelGoalMutation();
  const deleteMutation = useDeleteGoalMutation();
  const toggleItemMutation = useToggleChecklistItemMutation();
  const addItemMutation = useAddChecklistItemMutation();

  const isChecklist = goal?.measurement_type === 'checklist';
  const myPots = pots.filter((p) => p.goal === goalId);
  const checklistItems = goal?.checklist_items || [];
  const completedCount =
    goal?.checklist_completed_count ?? progress?.checklist_completed_count ?? 0;
  const totalCount = goal?.checklist_total_count ?? progress?.checklist_total_count ?? 0;

  if (goalShell.showBlockingSkeleton) {
    return <SectionSkeleton variant="card-grid" className="max-w-2xl" />;
  }

  if (!goal) {
    return null;
  }

  const isCompleted = goal.status === 'completed';
  const isCancelled = goal.status === 'cancelled';
  const isActive = goal.status === 'active' || goal.status === 'paused';
  const progressPct = Number(progress?.progress_pct ?? goal.progress_pct ?? 0);
  const monthly = Number(progress?.monthly_required ?? goal.monthly_required ?? goal.monthly_amount ?? 0);
  const dateLabel = formatDateLabelFromIso(goal.target_date);
  const targetLine = isChecklist
    ? dateLabel
    : pt.goals.detail.targetUntil
        .replace('{amount}', formatEUR(Number(goal.target_amount || 0)).replace(/\s*€$/, '').trim())
        .replace('{date}', dateLabel);

  const handleToggleItem = async (itemId) => {
    try {
      const result = await toggleItemMutation.mutateAsync({ goalId, itemId });
      if (result?.all_complete && isActive) {
        setCompleteSuggestOpen(true);
      }
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Não foi possível actualizar a tarefa.',
      );
    }
  };

  const handleAddTask = async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    try {
      await addItemMutation.mutateAsync({ goalId, title });
      setNewTaskTitle('');
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Não foi possível adicionar a tarefa.',
      );
    }
  };

  const handleConfirmComplete = async () => {
    setCompleteSuggestOpen(false);
    try {
      await completeMutation.mutateAsync(goalId);
      toast.success(pt.goals.detail.completedBanner);
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Não foi possível concluir o objectivo.',
      );
    }
  };

  const handleConfirmCancel = async () => {
    setCancelConfirmOpen(false);
    try {
      await cancelMutation.mutateAsync(goalId);
      toast.success(pt.goals.detail.cancelledBanner);
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Não foi possível cancelar o objectivo.',
      );
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmOpen(false);
    try {
      await deleteMutation.mutateAsync(goalId);
      navigate(paths.goals);
    } catch (err) {
      toast.error(
        typeof err === 'string' ? err : 'Não foi possível remover o objectivo.',
      );
    }
  };

  const statusBadge = isCompleted
    ? pt.goals.status.completed
    : isCancelled
      ? pt.goals.status.cancelled
      : null;

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-4 pb-8">
      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm dark:shadow-none">
        <div className="relative h-[230px]">
          {goal.image_url ? (
            <img src={goal.image_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-sage-500 to-sage-800" />
          )}
          <div
            className="absolute inset-0 bg-gradient-to-t from-[rgba(28,25,23,0.7)] to-transparent"
            aria-hidden
          />
          {statusBadge ? (
            <div className="absolute left-3.5 top-3.5 rounded-full bg-white/92 px-2.5 py-1 text-[11px] font-bold text-sage-700">
              {statusBadge}
            </div>
          ) : null}
          <div className="absolute bottom-4 left-[18px] right-[18px]">
            <h1 className="font-display text-[23px] font-bold leading-tight tracking-tight text-white">
              {goal.name}
            </h1>
            <p className="mt-1 text-[13.5px] text-white/88">{targetLine}</p>
          </div>
        </div>

        <div className="px-[18px] py-4">
          {!isChecklist && monthly > 0 ? (
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-muted-foreground">{pt.goals.detail.monthlyRow}</span>
              <span className="font-display text-lg font-bold tabular-nums text-sage-600 dark:text-sage-400">
                {formatEUR(monthly)}
              </span>
            </div>
          ) : null}
          {goal.why ? (
            <div
              className={
                !isChecklist && monthly > 0 ? 'mt-3.5 border-t border-border pt-3.5' : ''
              }
            >
              <p className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                {pt.goals.detail.whyLabel}
              </p>
              <p className="mt-1.5 font-display text-sm font-medium italic leading-relaxed text-foreground/80">
                “{goal.why}”
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isActive ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={pt.goals.detail.editAriaLabel}
              onClick={() => navigate(`${paths.goalsNew}?edit=${goalId}`)}
            >
              <Pencil className="mr-1 h-4 w-4" />
              Editar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setCancelConfirmOpen(true)}
              disabled={cancelMutation.isPending}
            >
              <XCircle className="mr-1 h-4 w-4" />
              {pt.goals.detail.cancelCta}
            </Button>
          </>
        ) : null}
        {isCancelled ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-danger-200 text-danger-700 hover:bg-danger-50"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={deleteMutation.isPending}
          >
            <Trash className="mr-1 h-4 w-4" />
            {pt.goals.detail.deleteCta}
          </Button>
        ) : null}
      </div>

      {isCompleted ? (
        <p
          className="rounded-xl border border-sage-700/50 bg-sage-50 px-3 py-2 text-sm text-sage-800 dark:border-sage-700 dark:bg-sage-900/30"
          role="status"
        >
          {pt.goals.detail.completedBanner}
        </p>
      ) : null}

      {isCancelled ? (
        <p
          className="rounded-xl border border-border bg-background px-3 py-2 text-sm text-muted-foreground"
          role="status"
        >
          {pt.goals.detail.cancelledBanner}
        </p>
      ) : null}

      <section className="rounded-2xl border border-border bg-card px-4 py-4">
        <h2 className="text-sm font-bold text-foreground">{pt.goals.detail.progressTitle}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {isChecklist
            ? pt.goals.checklist.progress
                .replace('{done}', String(completedCount))
                .replace('{total}', String(totalCount))
            : `${formatEUR(Number(progress?.accumulated || 0))} / ${formatEUR(Number(goal.target_amount || 0))}`}
        </p>
        <div className="mt-3">
          <ProgressBar value={progressPct} />
        </div>
        {!isChecklist && isActive ? (
          <p className="mt-3 text-xs text-muted-foreground">{pt.goals.detail.autoCompleteHint}</p>
        ) : null}
      </section>

      {isChecklist ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-base font-semibold text-foreground">
            {pt.goals.checklist.title}
          </h2>
          <ul className="flex flex-col gap-2">
            {checklistItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  disabled={!isActive || toggleItemMutation.isPending}
                  onClick={() => handleToggleItem(item.id)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left text-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {item.is_completed ? (
                    <CheckSquare className="h-5 w-5 shrink-0 text-sage-600" />
                  ) : (
                    <Square className="h-5 w-5 shrink-0 text-muted-foreground/70" />
                  )}
                  <span
                    className={
                      item.is_completed
                        ? 'text-muted-foreground line-through'
                        : 'text-foreground'
                    }
                  >
                    {item.title}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {isActive ? (
            <div className="flex items-center gap-2">
              <Input
                value={newTaskTitle}
                placeholder={pt.goals.checklist.taskPlaceholder}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTask();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={addItemMutation.isPending || !newTaskTitle.trim()}
                onClick={handleAddTask}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </section>
      ) : myPots.length === 0 ? (
        <p className="text-sm text-muted-foreground">{pt.pots.empty.body}</p>
      ) : (
        <section className="box-border w-full min-w-0 overflow-hidden rounded-xl border border-border bg-card">
          <div className="border-b border-border px-2.5 py-2 md:px-3.5 md:py-2.5">
            <h2 className="text-xs font-semibold text-muted-foreground md:text-sm">
              {pt.goals.detail.potTitle}
            </h2>
          </div>
          <ul className="divide-y divide-border">
            {myPots.map((pot) => (
              <PotRow key={pot.id} pot={pot} variant="list" />
            ))}
          </ul>
        </section>
      )}

      {progress?.milestones_reached && progress.milestones_reached.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-base font-semibold text-foreground">
            {pt.goals.detail.milestonesTitle}
          </h2>
          <ul className="flex flex-wrap gap-2">
            {progress.milestones_reached.map((m) => (
              <li
                key={m.level}
                className="rounded-full bg-sage-100 px-3 py-1 text-xs font-medium text-sage-700"
              >
                {m.level}%
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Dialog open={completeSuggestOpen} onOpenChange={setCompleteSuggestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.goals.checklist.completeSuggestTitle}</DialogTitle>
            <DialogDescription>{pt.goals.checklist.completeSuggestBody}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCompleteSuggestOpen(false)}>
              {pt.goals.checklist.completeDismiss}
            </Button>
            <Button onClick={handleConfirmComplete} disabled={completeMutation.isPending}>
              {pt.goals.checklist.completeConfirm}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.goals.detail.cancelConfirmTitle}</DialogTitle>
            <DialogDescription>{pt.goals.detail.cancelConfirmBody}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCancelConfirmOpen(false)}>
              {pt.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={cancelMutation.isPending}
            >
              {pt.goals.detail.cancelCta}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pt.goals.detail.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>
              {isChecklist
                ? pt.goals.detail.deleteConfirmBody
                : pt.goals.detail.deleteConfirmBodyWithPot}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)}>
              {pt.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {pt.goals.detail.deleteCta}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GoalDetail;
